import styled from '@emotion/styled';
import {media, spacing} from '@nfq/react-grid';

import {getFontSize, getLineHeight} from './utils';

import type {FontSizes} from './utils';

interface TextProps {
    $align?: 'center' | 'left' | 'right';
}

export const H1 = styled.h1<TextProps>`
    color: ${({theme}) => theme.colors.textPrimary};
    font-family: ${({theme}) => theme.fonts.Poppins};
    font-size: 4rem;
    font-weight: 700;
    hyphens: auto;
    letter-spacing: -0.02em;
    line-height: 1;
    margin: 0;
    padding: 0;
    text-align: ${({$align}) => $align ?? 'left'};
    text-wrap: balance;

    ${media('md')} {
        font-size: 6.4rem;
    }
`;

export const H2 = styled.h2<TextProps>`
    color: ${({theme}) => theme.colors.textPrimary};
    font-family: ${({theme}) => theme.fonts.Poppins};
    font-size: 3.4rem;
    font-weight: 700;
    hyphens: auto;
    letter-spacing: -0.02em;
    line-height: 1.1;
    margin: 0;
    padding: 0;
    text-align: ${({$align}) => $align ?? 'left'};
    text-wrap: balance;

    ${media('md')} {
        font-size: 5.1rem;
    }
`;

export const H3 = styled.h3<TextProps>`
    color: ${({theme}) => theme.colors.textPrimary};
    font-family: ${({theme}) => theme.fonts.Poppins};
    font-size: 3rem;
    font-weight: 700;
    hyphens: auto;
    letter-spacing: -0.02em;
    line-height: 1.2;
    margin: 0;
    padding: 0;
    text-align: ${({$align}) => $align ?? 'left'};
    text-wrap: balance;

    ${media('md')} {
        font-size: 4rem;
    }
`;

export const H4 = styled.h4<TextProps>`
    color: ${({theme}) => theme.colors.textPrimary};
    font-family: ${({theme}) => theme.fonts.Poppins};
    font-size: 2.5rem;
    font-weight: 700;
    hyphens: auto;
    letter-spacing: -0.02em;
    line-height: 1.25;
    margin: 0;
    padding: 0;
    text-align: ${({$align}) => $align ?? 'left'};
    text-wrap: balance;

    ${media('md')} {
        font-size: 3.2rem;
    }
`;

export const H5 = styled.h5<TextProps>`
    color: ${({theme}) => theme.colors.textPrimary};
    font-family: ${({theme}) => theme.fonts.Poppins};
    font-size: 2.2rem;
    font-weight: 700;
    hyphens: auto;
    letter-spacing: -0.01em;
    line-height: 1.3;
    margin: 0;
    padding: 0;
    text-align: ${({$align}) => $align ?? 'left'};
    text-wrap: balance;

    ${media('md')} {
        font-size: 2.5rem;
    }
`;

export const H6 = styled.h6<TextProps>`
    color: ${({theme}) => theme.colors.textPrimary};
    font-family: ${({theme}) => theme.fonts.Poppins};
    font-size: 1.9rem;
    font-weight: 700;
    hyphens: auto;
    letter-spacing: -0.01em;
    line-height: 1.35;
    margin: 0;
    padding: 0;
    text-align: ${({$align}) => $align ?? 'left'};
    text-wrap: balance;

    ${media('md')} {
        font-size: 2rem;
    }
`;

interface ParagraphProps {
    $align?: 'center' | 'left' | 'right';
    $size?: FontSizes;
}

export const P = styled.p<ParagraphProps>`
    color: ${({theme}) => theme.colors.textSecondary};
    font-family: ${({theme}) => theme.fonts.Poppins};
    font-size: ${getFontSize};
    font-weight: 400;
    hyphens: auto;
    line-height: ${getLineHeight};
    margin: 0;
    padding: 0;
    text-align: ${({$align}) => $align ?? 'left'};
`;

export const SmallP = styled.p<TextProps>`
    color: ${({theme}) => theme.colors.textSecondary};
    font-family: ${({theme}) => theme.fonts.Poppins};
    font-size: 1.4rem;
    font-weight: 400;
    hyphens: auto;
    line-height: 1.4;
    margin: 0;
    padding: 0;
    text-align: ${({$align}) => $align ?? 'left'};
`;

export const Label = styled.p<ParagraphProps>`
    color: ${({theme}) => theme.colors.textPrimary};
    font-family: ${({theme}) => theme.fonts.Geist};
    font-size: 1.4rem;
    letter-spacing: 0.1em;
    line-height: 1.4;
    margin: 0;
    padding: 0;
    text-align: ${({$align}) => $align ?? 'left'};
    text-transform: uppercase;
`;

export const Li = styled.li`
    color: ${({theme}) => theme.colors.textSecondary};
    font-family: ${({theme}) => theme.fonts.Poppins};
    font-size: 1.6rem;
    line-height: 1.5;
    margin: 0;
    padding: 0;
`;

export const Ul = styled.ul`
    display: flex;
    flex-direction: column;
    gap: ${spacing(2)};
    line-height: 1.6;
    list-style-type: disc;
    margin: 0;
    padding: 0 0 0 ${spacing(6)};
`;

export const Ol = styled.ol`
    counter-reset: item;
    display: flex;
    flex-direction: column;
    gap: ${spacing(2)};
    line-height: 1.6;
    margin: 0;
    padding: 0 0 0 ${spacing(6)};
`;